import {
  createAction,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import {
  LiquidationsList,
  MonthSummary,
  SettlementsDetailList,
} from '../../interfaces/ReportingInterface/ReportingInterface';
import { getPaidSummary } from './reportingAPI';

interface SettlementData {
  data: {
    settlements: LiquidationsList[];
    settlementDetail: SettlementsDetailList | null;
    settlementDetailList: SettlementsDetailList[];
    currentSettlements: LiquidationsList | null;
    monthSummaries: MonthSummary[];
  };
}

const initialState: SettlementData = {
  data: {
    settlements: [],
    settlementDetail: null,
    settlementDetailList: [],
    currentSettlements: null,
    monthSummaries: [],
  },
};

export const setMonthSummariesPartial = createAction<MonthSummary[]>(
  'reporting/setMonthSummariesPartial',
);

export const fetchMonthSummaries = createAsyncThunk(
  'reporting/fetchMonthSummaries',
  async (_, thunkAPI) => {
    const endDate = dayjs().endOf('month');
    const startDate = endDate.subtract(16, 'month').startOf('month');

    let currentDate = endDate;
    const results: MonthSummary[] = [];
    let hasDispatchedFirstEight = false;

    while (currentDate.isAfter(startDate) || currentDate.isSame(startDate)) {
      const to_date = currentDate.format('YYYY-MM-DD');
      const from_date = currentDate
        .subtract(1, 'month')
        .startOf('month')
        .format('YYYY-MM-DD');
      const data = await getPaidSummary({ from_date, to_date });

      if (data) {
        const summaries = [...data.data.dashboards_summaries].reverse();
        results.push(...summaries);

        // devuelve 8 meses para no tener que esperar al final del bucle.
        if (!hasDispatchedFirstEight && results.length >= 8) {
          thunkAPI.dispatch(setMonthSummariesPartial(results.slice(0, 8)));
          hasDispatchedFirstEight = true;
        }
      }

      currentDate = currentDate.subtract(2, 'month').endOf('month');
    }

    return results;
  },
);

// Refresca SOLO el tramo mes actual + mes anterior (la primera llamada de
// `fetchMonthSummaries`) y lo fusiona en `monthSummaries` sin re-pedir el
// histórico completo.
export const refreshRecentMonthSummary = createAsyncThunk(
  'reporting/refreshRecentMonthSummary',
  async () => {
    const endDate = dayjs().endOf('month');
    const to_date = endDate.format('YYYY-MM-DD');
    const from_date = endDate
      .subtract(1, 'month')
      .startOf('month')
      .format('YYYY-MM-DD');
    const data = await getPaidSummary({ from_date, to_date });
    const summaries = data?.data?.dashboards_summaries ?? [];
    return [...summaries].reverse();
  },
);

export const settlementSlice = createSlice({
  name: 'settlement',
  initialState,
  reducers: {
    saveSettlements: (state, action: PayloadAction<LiquidationsList[]>) => {
      state.data.settlements = action.payload;
    },
    saveSettlementDetail: (
      state,
      action: PayloadAction<SettlementsDetailList>,
    ) => {
      state.data.settlementDetail = action.payload;
    },
    saveSettlementDetailList: (
      state,
      action: PayloadAction<SettlementsDetailList[]>,
    ) => {
      state.data.settlementDetailList = action.payload;
    },
    selectCurrentSettlements: (
      state,
      action: PayloadAction<LiquidationsList>,
    ) => {
      state.data.currentSettlements = action.payload;
    },
    clearMonthSummaries: (state) => {
      state.data.monthSummaries = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setMonthSummariesPartial, (state, action) => {
      state.data.monthSummaries = action.payload;
    });
    builder.addCase(fetchMonthSummaries.fulfilled, (state, action) => {
      state.data.monthSummaries = action.payload;
    });
    builder.addCase(refreshRecentMonthSummary.fulfilled, (state, action) => {
      if (!action.payload.length) return;
      // Fusiona por `date`: reemplaza los meses devueltos y conserva el resto.
      const byDate = new Map(
        state.data.monthSummaries.map((summary) => [summary.date, summary]),
      );
      action.payload.forEach((summary) => byDate.set(summary.date, summary));
      state.data.monthSummaries = Array.from(byDate.values()).sort((a, b) => (a.date < b.date ? 1 : -1));
    });
  },
});

export const {
  saveSettlements,
  saveSettlementDetail,
  saveSettlementDetailList,
  selectCurrentSettlements,
  clearMonthSummaries,
} = settlementSlice.actions;

export default settlementSlice.reducer;
